import { useSearchInputValueDebounce } from 'hooks/useSearchInputValueDebounce';
import type { IntlKey } from 'locales';
import { type ChangeEvent, type FC, type PropsWithChildren, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import './full-text-search.scss';

const DEFAULT_DEBOUNCE = 1500;

interface FullTextSearchProps {
  value?: string;
  onChange: (value?: string) => void;
  placeholder?: IntlKey;
  debounce?: boolean;
  debounceDelayMs?: number;
}

export const FullTextSearch: FC<PropsWithChildren<FullTextSearchProps>> = ({
  children,
  value,
  onChange,
  placeholder,
  debounce = false,
  debounceDelayMs = DEFAULT_DEBOUNCE,
}) => {
  const { formatMessage } = useIntl();
  const shouldDebounce = debounce === true;
  const emitOnChangeOnlyWhenActualChange = useCallback(
    (newValue?: string) => {
      if (newValue) {
        if (newValue !== value) {
          onChange(newValue);
        }
      } else {
        if (value) {
          onChange(undefined);
        }
      }
    },
    [onChange, value],
  );

  const debounceHook = useSearchInputValueDebounce(value ?? '', debounceDelayMs);

  const _onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (shouldDebounce) {
        debounceHook.setInputValue(e.target.value);
      } else {
        emitOnChangeOnlyWhenActualChange(e.target.value);
      }
    },
    [debounceHook, shouldDebounce, emitOnChangeOnlyWhenActualChange],
  );

  useEffect(() => {
    if (shouldDebounce) {
      emitOnChangeOnlyWhenActualChange(debounceHook.debouncedInputValue || undefined);
    }
  }, [debounceHook.debouncedInputValue, shouldDebounce, emitOnChangeOnlyWhenActualChange]);

  const displayValue = shouldDebounce ? debounceHook.inputValue : value;

  return (
    <div className="input-group input-group-lg border-start border-opacity-40 d-md-flex flex-grow full-text-search">
      <span className="input-icon-start">
        <i className="icon">search</i>
      </span>
      <input
        type="search"
        className="form-control form-control-xl"
        placeholder={formatMessage({ id: placeholder ?? 'blotter.filter.placeholder.quickSearch' })}
        data-e2e="quick-search-term"
        value={displayValue}
        onChange={_onChange}
      />
      {children}
    </div>
  );
};
